import styled from '@emotion/styled';
import { SubnavContainer } from '@landsbankinn/shared/containers/SubnavContainer';
import { MetaContainer } from '@landsbankinn/shared/prismicContainers/MetaContainer';
import { SlicesContainer } from '@landsbankinn/shared/prismicContainers/SlicesContainer';
import { responseFont } from '@landsbankinn/ui';
import { useMergedQueries } from '@landsbankinn/utils';
import type { GetStaticPropsContext } from 'next';
import { useRouter } from 'next/router';
import React from 'react';
import { Config } from 'lib/config';
import { buildBreadcrumbs } from 'lib/linkResolver';
import { pageQueries } from '../graphql/prismic/pageQuery';
import { getStaticPropsData } from '../lib/getStaticProps';
import type { LBPageProps } from './_app';
import ErrorPage from './_error';

const Container = styled.div`
  & > div > section {
    position: relative;
    z-index: 2;
  }
`;

const StyledSubNavContainer = styled(SubnavContainer)`
  ul {
    @media (max-width: 1400px) {
      margin-left: auto;
      margin-right: 58px;
    }
  }
  li {
    ${responseFont(12, 16)}
    @media (max-width: 1200px) {
      padding-left: 10px;
      padding-right: 10px;
      ${responseFont(12, 14)}
    }
    @media (max-width: 1200px) {
      padding-left: 5px;
      padding-right: 5px;
    }
  }
  h2 {
    @media (max-width: 1400px) {
      margin-left: 80px;
      width: 166px;
    }
    @media (max-width: 1300px) {
    }
    @media (max-width: 1200px) {
      width: auto;
    }
  }
`;

export function usePageData(page: any, lang: any) {
  const res = useMergedQueries(pageQueries, {
    variables: { uid: page?.uid, lang },
  });

  return res.data;
}

export async function getPageData({
  url = '',
  lang = 'is',
  pages,
  client,
}: any) {
  const page = pages.find((p: any) => p.url === url) || null;
  if (page) {
    for (const pageQuery of pageQueries) {
      await client.query({
        query: pageQuery,
        variables: { lang, uid: page.uid },
      });
    }
  }
  return page;
}

export function getStaticPageProps(opts: any = {}) {
  const { lang = 'is', prefix = '' } = opts;
  return async function getStaticProps(props: GetStaticPropsContext) {
    props.params = props.params || {};
    props.params.page = opts.page ?? props.params.page;
    props.params.lang = lang;
    const { extractProps, pages, client } = await getStaticPropsData(props);
    const url = `${prefix}/${[].concat(props.params.page as any).join('/')}`;
    let page = pages.find((p) => p.url === url) || false;
    if (!page) {
      page = pages.find((p) => p.url === '/404-skilabod') || false;
    }

    if (page) {
      for (const pageQuery of pageQueries) {
        await client.query({
          query: pageQuery,
          variables: { lang, uid: page.uid },
        });
      }
    }

    return {
      props: {
        page,
        ...extractProps(),
        lang,
      },
      notFound: !page,
      revalidate: Config.REVALIDATION_TIMEOUT,
    };
  };
}

function getRootPage(page: any) {
  let n = page;
  let rootPage = page;
  while (n) {
    const parentItems = (n?.sub_navigation || []).filter((x: any) => !!x?.link);
    rootPage = n;
    n = parentItems.length ? null : n?.parent;
  }
  if (
    !rootPage?.parentPage &&
    !(rootPage?.sub_navigation || []).filter((x: any) => !!x?.link).length
  ) {
    rootPage = page;
  }
  return rootPage;
}

export default function Page({
  page,
  lang,
  preview,
  website,
  pages,
}: LBPageProps) {
  const router = useRouter();

  const res = useMergedQueries(pageQueries, {
    variables: { uid: page?.uid, lang },
  });

  const data = res.data;

  if ((!preview && !page) || (preview && !router.isFallback && !page)) {
    return <ErrorPage statusCode={404} />;
  }

  if (router.isFallback || res.loading) {
    return (
      <div>
        Loading… data: {data ? '1' : '0'} - loading: {res.loading ? '1' : '0'}
      </div>
    );
  }

  const rootPage = getRootPage(data?.page);
  const hideSubNav = data?.page?.hide_sub_nav === true;
  const isFrontPage = data?.page?._meta?.uid === 'index' || false;

  return (
    <Container data-wio-id={data?.page?._meta?.id}>
      <MetaContainer
        canonicalUrlBase={Config.CANONICAL_URL_BASE ?? ''}
        data={data?.page}
        breadcrumbs={buildBreadcrumbs(page, pages)}
        defaultTitle={website?.title}
        defaultImage={website?.seo_image}
        defaultDescription={website?.seo_description}
        isFrontPage={isFrontPage}
        siteName="Ársskýrsla Landsbankans"
      />
      {!hideSubNav && (
        <StyledSubNavContainer
          titleWidth="274px"
          page={rootPage}
          active={data?.page}
          ctaLink={data?.page?.cta_button}
          ctaText={data?.page?.cta_button_text}
          ctaMobileText={data?.page?.cta_mobile_button_text}
        />
      )}
      <SlicesContainer data={data.page?.body} template="article" />
    </Container>
  );
}

export const getStaticProps = getStaticPageProps({ lang: 'is' });

export async function getStaticPaths() {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { pages } = await getStaticPropsData({ params: { lang: 'is' } });

  const paths = pages
    .filter((x: any) => {
      // only page
      if (process.env.ONLY_PAGE) {
        const test = new RegExp(process.env.ONLY_PAGE);
        return !!x?.url?.match(test);
      }
      return true;
    })
    .map((page: any) => page.url);

  return {
    paths,
    fallback: 'blocking',
  };
}
